import * as z from 'zod/mini'
import { keyedIssue } from '../../errors/validation-keys'
import { messageField, queryBoolean } from '../../schemas/common'
import { reservationTextFields } from '../../schemas/reservation'

/**
 * A premise's participations, narrowed to one event or one customer (at least one is required).
 * `EventParticipationService.getEventParticipations` always scopes the result to the premise.
 */
export const getEventParticipationsQuery = z
  .object({
    eventId: z.optional(z.uuid()),
    customerId: z.optional(z.uuid()),
    confirmed: z.optional(queryBoolean()),
    attended: z.optional(queryBoolean()),
  })
  .check(
    z.superRefine((value, ctx) => {
      if (!value.eventId && !value.customerId) {
        ctx.addIssue(keyedIssue('validation.eventOrCustomerRequired', ['eventId']))
      }
    }),
  )
export type GetEventParticipationsQuery = z.infer<typeof getEventParticipationsQuery>

/**
 * A manager adds a client. Both ids are resolved within the premise (404 for an id from elsewhere). With
 * `notifyCustomer` a client who is now signed up gets the confirmation (E5), with `message` in it.
 */
export const createEventParticipationBody = z.object({
  eventId: z.uuid(),
  customerId: z.uuid(),
  confirmed: z.optional(z.boolean()),
  notifyCustomer: z.optional(z.boolean()),
  message: z.optional(messageField),
})
export type CreateEventParticipationBody = z.infer<typeof createEventParticipationBody>

export const editEventParticipationBody = z.object({
  confirmed: z.optional(z.boolean()),
  attended: z.optional(z.boolean()),
})
export type EditEventParticipationBody = z.infer<typeof editEventParticipationBody>

/**
 * The premise cancels a sign-up: it stays, marked cancelled, and frees its place. Refused once the event started
 * (`eventParticipation.eventStarted`) or if already cancelled (`eventParticipation.cancelled`).
 */
export const cancelEventParticipationBody = z.object({
  notifyCustomer: z.boolean(),
  /** Goes into the cancel e-mail. */
  reason: z.optional(reservationTextFields.cancellationReason),
})
export type CancelEventParticipationBody = z.infer<typeof cancelEventParticipationBody>

/** A participation URL, looked up by `EventParticipationGuard` within the premise. */
export const eventParticipationParams = z.object({ eventParticipationId: z.uuid() })
export type EventParticipationParams = z.infer<typeof eventParticipationParams>
